import {
	ClassDescriptor,
	EnumDescriptor,
	FieldDescriptor,
	MethodDescriptor, ParameterInfo, ParamListType,
	SpecificConstructor
} from "common";
import {ClassDescriptorClass} from "./ClassDescriptorClass";
import "reflect-metadata"

declare global {

	interface Window {
		// To temporarily store the API functions before they are registered
		ChatGPTAPIFunctions: Map<string, SpecificConstructor>;
		ChatGPTAPIEnums: Map<string, EnumDescriptor<any>>;
		ChatGPTAPIClasses: Map<string, ClassDescriptor>
	}
}

export const ChatGPTMethodDescriptions = new Map<string, MethodDescriptor>();
export const ChatGPTMethodDescriptors = new Map<string, object>();
// export let ChatGPTAPIFunctions : Map<string, SpecificConstructor>;
export let ChatGPTAPIEnums : Map<string, EnumDescriptor<any>>;

export const enumRegistry: Record<string, any> = {};

if ( window.ChatGPTAPIEnums)
{
	// If the API functions have already been registered, use them
	// RegisterWithAPI can be called before this file is initialized.
	// Therefore, once we're here, we can assign the functions to the global variable.
	ChatGPTAPIEnums = window.ChatGPTAPIEnums;
}



class EnumDescriptorClass<T> implements EnumDescriptor<T>
{
	enumEntries: [string, T][];
	enumName: string;

	private isEnum<T>(obj: any): obj is { [key: string]: T }
	{
		return obj !== null && typeof obj === 'object';
	}

	constructor(enumType: { [key: string]: T }, enumName: string)
	{
		if (!this.isEnum<any>(enumType))
		{
			throw new Error('Provided value is not an enum-like object.');
		}

		this.enumEntries = Object.entries(enumType) as [string, T][];
		this.enumName = enumName;
	}
}

export function RegisterEnumWithAPI(enumObj: any, enumName: string)
{
	if (!window.ChatGPTAPIEnums)
	{
		window.ChatGPTAPIEnums = new Map<string, EnumDescriptor<any>>();
	}
	// console.log("Registering enum " + enumName + " with API");

	const enumDescriptorClass = new EnumDescriptorClass(enumObj, enumName);
	window.ChatGPTAPIEnums.set(enumName, enumDescriptorClass);
	
	// Add to registry for exports
	enumRegistry[enumName] = enumObj;
}

export function Description(desc: string, params: ParamListType, returns: string)
{
	return function (target: any, key: string, descriptor: PropertyDescriptor)
	{
		const originalMethod = descriptor.value;

		// Convert old params format to new parameterInfo format
		const parameterInfo: { [key: string]: ParameterInfo } = {};
		for (const [paramName, paramType] of Object.entries(params)) 
		{
			parameterInfo[paramName] = {
				type: paramType,
				optional: paramType.includes("optional"),
				description: paramType
			};
		}

		ChatGPTMethodDescriptions.set(key, {description: desc, parameterInfo: parameterInfo, returnType: returns});
		ChatGPTMethodDescriptors.set(key, originalMethod);

		descriptor.value = function (...args: any[])
		{
			return originalMethod.apply(this, args);
		};

		return descriptor;
	};
}

export function RegisterActionWithAPI(name: string)
{
	return function (constructor: SpecificConstructor)
	{
		Reflect.defineMetadata("originalClassName", name, constructor);
		if ( ! window.ChatGPTAPIFunctions)
		{
		// RegisterWithAPI can be called before this file is initialized.
		// Therefore store the functions in a temporary variable until the API is initialized.
			window.ChatGPTAPIFunctions = new Map<string, SpecificConstructor>();
		}
		// console.log("Registering " + constructor.name + " with API")
		window.ChatGPTAPIFunctions.set(name, constructor);
	};
}

export function RegisterClassWithAPI(name: string, params: ParamListType, description: string = "", allowConstructor = true)
{
	return function (constructor: SpecificConstructor)
	{
		if (!window.ChatGPTAPIClasses)
		{
			// RegisterWithAPI can be called before this file is initialized.
			// Therefore store the functions in a temporary variable until the API is initialized.
			window.ChatGPTAPIClasses = new Map<string, ClassDescriptor>();
		}

		let classDescriptor = Reflect.getMetadata("methods", constructor)
		if ( !classDescriptor)
		{
			classDescriptor = new ClassDescriptorClass(constructor, {}, description);
			Reflect.defineMetadata("methods", classDescriptor, constructor);
		}
		classDescriptor.name = name;
		classDescriptor.constructorParameters = params;
		classDescriptor.description = description;
		classDescriptor.allowConstructor = allowConstructor;

		window.ChatGPTAPIClasses.set(name, classDescriptor);
	}
}


export function APIField(config: { description?: string; type: string })
{
	return function (target: any,
					 propertyKey: string,
					 descriptor?: PropertyDescriptor
	)
	{
		let classDescriptor = Reflect.getMetadata("methods", target.constructor)
		if ( !classDescriptor)
		{
			classDescriptor = new ClassDescriptorClass(target.constructor, {}, "");
			Reflect.defineMetadata("methods", classDescriptor, target.constructor);
		}

		const hasGetter = descriptor && typeof descriptor.get === "function";
		const hasSetter = descriptor && typeof descriptor.set === "function";

		classDescriptor.fields[propertyKey] = {
			description: config.description || "",
			type: config.type,
			hasGetter,
			hasSetter,
		} as FieldDescriptor;
	}
}

export function APIMethod(config: { description: string; parameters: ParamListType; returnType: string })
{
	return function(target: any, propertyKey: string, descriptor: PropertyDescriptor)
	{
		if ( target.constructor.name == "Function")
		{
			// This is a static method
			console.error("Static methods are not supported")
			return;
		}
		let classDescriptor = Reflect.getMetadata("methods", target.constructor)
		if ( !classDescriptor)
		{
			classDescriptor = new ClassDescriptorClass(target.constructor, {}, "");
			Reflect.defineMetadata("methods", classDescriptor, target.constructor);
		}

		// Convert old params format to new parameterInfo format
		const parameterInfo: { [key: string]: ParameterInfo } = {};
		for (const [paramName, paramType] of Object.entries(config.parameters)) 
		{
			parameterInfo[paramName] = {
				type: paramType,
				optional: paramType.includes("optional"),
				description: paramType
			};
		}

		// Assign the new method descriptor
		classDescriptor.methods[propertyKey] = new class implements MethodDescriptor
		{
			description = config.description;
			parameterInfo = parameterInfo;
			returnType = config.returnType;
		};
	}
}
